import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, XCircle, Clock, MinusCircle, FlaskConical, Mail, MessageSquareText, ArrowRight } from "lucide-react";
import { V } from "@/lib/vocab";

const PILL = {
  delivered: ["bg-emerald-50 text-emerald-700", CheckCircle2, "Delivered"],
  sent: ["bg-sand-100 text-ink/60", Clock, "Sent · awaiting receipt"],
  failed: ["bg-danger-50 text-danger-700", XCircle, "Failed"],
  undelivered: ["bg-danger-50 text-danger-700", XCircle, "Not delivered"],
  simulated: ["bg-amber-50 text-amber-700", FlaskConical, "Simulated (dry run)"],
  skipped: ["bg-sand-100 text-ink/50", MinusCircle, "Skipped"],
};
const EMAIL_PILL = { sent: ["bg-emerald-50 text-emerald-700", CheckCircle2, "Emailed"], failed: ["bg-danger-50 text-danger-700", XCircle, "Email failed"] };

const Pill = ({ row }) => {
  const [cls, Icon, label] = (row.channel === "email" ? EMAIL_PILL[row.status] : null) || PILL[row.status] || PILL.sent;
  return (
    <span data-testid={`recipient-status-${row.id}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold whitespace-nowrap ${cls}`}>
      <Icon className="h-3 w-3" /> {row.fallback ? "Emailed instead" : label}
    </span>
  );
};

// "Who got it" — one row per resident per channel, failures first, with the reason in plain English.
export const RecipientsPanel = ({ tenantId, messageId }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let live = true;
    const load = () => api.get(`/tenants/${tenantId}/broadcasts/${messageId}/recipients`).then((r) => live && setData(r.data)).catch(() => live && setErr("Couldn't load the delivery list"));
    load();
    const t = setInterval(load, 15000);
    return () => { live = false; clearInterval(t); };
  }, [tenantId, messageId]);

  if (err) return <p className="text-xs text-danger-600 mt-3">{err}</p>;
  if (!data) return <div className="mt-3 flex items-center gap-2 text-xs text-ink/50"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>;
  if (!data.tracked) return <p data-testid="recipients-untracked" className="mt-3 text-xs text-ink/50">Per-{V.lc("person")} delivery wasn't recorded for this message (sent before receipts were tracked).</p>;
  const c = data.counts || {};
  const line = [c.sms_delivered && `${c.sms_delivered} delivered`, (c.sms_failed || 0) + (c.sms_undelivered || 0) ? `${(c.sms_failed || 0) + (c.sms_undelivered || 0)} failed` : null,
    c.sms_sent && `${c.sms_sent} awaiting receipt`, c.sms_simulated && `${c.sms_simulated} simulated`, c.email_sent && `${c.email_sent} emailed`,
    c.email_failed && `${c.email_failed} email failed`, (c.sms_skipped || 0) + (c.email_skipped || 0) ? `${(c.sms_skipped || 0) + (c.email_skipped || 0)} skipped` : null].filter(Boolean).join(" · ");
  return (
    <div data-testid={`recipients-panel-${messageId}`} className="mt-3 rounded-xl border border-sand-200 bg-sand-50/60 overflow-hidden">
      <div className="px-3 py-2 text-[11px] font-semibold text-ink/55 border-b border-sand-200 flex flex-wrap items-center justify-between gap-2">
        <span data-testid="recipients-summary">{line || "No recipients"}</span>
        {data.dry_run && <span className="text-amber-700">Preview is in dry-run — real receipts arrive in production</span>}
      </div>
      <ul className="divide-y divide-sand-200 max-h-80 overflow-y-auto">
        {data.rows.map((r) => (
          <li key={r.id} data-testid={`recipient-row-${r.id}`} className="flex items-start justify-between gap-3 px-3 py-2 text-xs bg-white">
            <div className="min-w-0 flex items-start gap-2">
              {r.channel === "email" ? <Mail className="h-3.5 w-3.5 mt-0.5 text-ink/40 shrink-0" /> : <MessageSquareText className="h-3.5 w-3.5 mt-0.5 text-ink/40 shrink-0" />}
              <div className="min-w-0">
                <div className="font-semibold text-ink truncate">{r.name}{r.unit ? <span className="font-normal text-ink/45"> · {r.unit}</span> : null}</div>
                {(r.error || r.fallback_email) && (
                  <div className="text-ink/55 leading-snug">
                    {r.error}
                    {r.fallback_email && <span className="inline-flex items-center gap-1 ml-1 text-emerald-700"><ArrowRight className="h-3 w-3" /> {r.fallback_email === "sent" ? "emailed instead" : "email fallback failed too"}</span>}
                  </div>
                )}
              </div>
            </div>
            <Pill row={r} />
          </li>
        ))}
      </ul>
    </div>
  );
};
